import { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { recordUserInteraction, getFavorites, toggleFavorite } from "wasp/client/operations";
import { useQuery, useAction } from "wasp/client/operations";

const LIKED_RECIPES_KEY = "nura_liked_recipes";

export interface LikedRecipe {
  id: string;
  title: string;
  imageUrl: string; // Required field
  description?: string;
  difficulty?: string;
  cuisine?: string;
  totalMinutes?: number;
  tags?: string[];
  likedAt: string;
}

export function useLikedRecipes() {
  const queryClient = useQueryClient();
  // Fetch favorites from database
  const { data: dbFavorites = [], isLoading, refetch } = useQuery(getFavorites);
  const toggleFavoriteAction = useAction(toggleFavorite);

  // Transform database recipes to LikedRecipe format
  const likedRecipes = useMemo(() => {
    return dbFavorites.map((recipe: any) => ({
      id: recipe.id,
      title: recipe.title,
      imageUrl: recipe.heroImageUrl || recipe.imageUrl || "",
      description: recipe.description,
      difficulty: recipe.difficulty,
      cuisine: recipe.cuisine,
      totalMinutes: recipe.totalMinutes,
      tags: recipe.tags || [],
      likedAt: recipe.createdAt || new Date().toISOString(),
    }));
  }, [dbFavorites]);

  const likedRecipeIds = useMemo(() => {
    return likedRecipes.map((r) => r.id);
  }, [likedRecipes]);

  // Migration: Load liked recipes from localStorage on mount and sync to DB
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LIKED_RECIPES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as LikedRecipe[];
        // TODO: Migrate localStorage favorites to database (one-time migration)
        console.log(`[useLikedRecipes] Found ${parsed.length} localStorage favorites - migration needed`);
      }
    } catch (error) {
      console.warn("Failed to load liked recipes from localStorage:", error);
    }
  }, []);

  // Helper function to record interaction with server for AI learning
  const recordInteraction = useCallback(
    async (
      recipe: Partial<LikedRecipe> & { id: string; title: string },
      action:
        | "liked"
        | "skipped"
        | "saved"
        | "maybe"
        | "viewed"
        | "searched"
        | "cooked"
        | "shared",
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      try {
        await recordUserInteraction({
          recipeId: recipe.id,
          action,
          recipe: {
            title: recipe.title,
            cuisine: recipe.cuisine,
            difficulty: recipe.difficulty,
            prepTime: additionalData?.prepTime,
            calories: additionalData?.calories,
            tags: additionalData?.tags,
            ingredients: additionalData?.ingredients,
          },
          context: {
            timeOfDay: new Date().getHours().toString(),
            dayOfWeek: new Date().getDay().toString(),
            sessionDuration: 0, // Could be enhanced to track actual session time
            previousSwipes: 0, // Could be enhanced to track swipe count
          },
        });
      } catch (error) {
        // Silent fail - don't break UX if server action fails
        console.warn("Failed to record interaction for AI learning:", error);
      }
    },
    [],
  );

  const likeRecipe = useCallback(
    async (
      recipe: Partial<LikedRecipe> & {
        id: string;
        title: string;
        imageUrl: string;
      },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      try {
        // Detect external recipes (numeric IDs)
        const isExternal = /^\d+$/.test(recipe.id);

        // Toggle favorite in database (auto-imports external recipes)
        await toggleFavoriteAction({ recipeId: recipe.id, isExternal });

        // Refetch to update UI
        await refetch();

        // 🎯 CRITICAL: Record interaction for AI learning (Phase 2 fix)
        recordInteraction(recipe, "liked", additionalData);
        // UX feedback
        toast.success("Added to favorites", { description: recipe.title });
      } catch (error) {
        console.error("Failed to like recipe:", error);
        // Fallback: cache-first optimistic like using Discover cache data
        try {
          // Attempt to optimistically patch the getFavorites cache so UI reflects the like.
          // We use the Wasp query cache key shape: [relativeQueryPath] for void args.
          const key = (getFavorites as any).queryCacheKey as unknown[];
          if (Array.isArray(key)) {
            queryClient.setQueryData<any[]>(key, (old = []) => {
              if (old.some((r) => String((r as any)?.id) === String(recipe.id))) return old;
              return [
                ...old,
                {
                  id: recipe.id,
                  title: recipe.title,
                  imageUrl: recipe.imageUrl,
                  heroImageUrl: (recipe as any).heroImageUrl ?? recipe.imageUrl,
                  description: (recipe as any).description,
                  difficulty: (recipe as any).difficulty,
                  cuisine: (recipe as any).cuisine,
                  totalMinutes: (recipe as any).totalMinutes,
                  tags: (recipe as any).tags ?? [],
                  // Mark as client-cached so we can reconcile later
                  __clientCached: true,
                },
              ];
            });
            toast.success("Saved to favorites (cached)", {
              description: "Will sync when credits are available.",
            });
          }
        } catch {}
        const message = (error as any)?.message || "Added to local favorites cache.";
        console.warn(message);
      }
    },
    [toggleFavoriteAction, refetch, recordInteraction, queryClient],
  );

  const unlikeRecipe = useCallback(
    async (
      recipeId: string,
      recipeData?: Partial<LikedRecipe> & { id: string; title: string },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      try {
        // Toggle favorite in database (will remove it)
        await toggleFavoriteAction({ recipeId });

        // Refetch to update UI
        await refetch();

        // 🎯 CRITICAL: Record unlike as 'skipped' for AI learning
        if (recipeData) {
          recordInteraction(recipeData, "skipped", additionalData);
        } else {
          const found = likedRecipes.find((r) => r.id === recipeId);
          if (found) {
            recordInteraction(found, "skipped", additionalData);
          }
        }
        // UX feedback
        toast.info("Removed from favorites");
      } catch (error) {
        console.error("Failed to unlike recipe:", error);
        // Fallback: remove from cached favorites if present
        try {
          const key = (getFavorites as any).queryCacheKey as unknown[];
          if (Array.isArray(key)) {
            queryClient.setQueryData<any[]>(key, (old = []) =>
              old.filter((r) => String((r as any)?.id) !== String(recipeId)),
            );
            toast("Removed from cached favorites");
          }
        } catch {}
      }
    },
    [toggleFavoriteAction, refetch, likedRecipes, recordInteraction, queryClient],
  );

  // New function specifically for recording skips (swipe left, etc.)
  const skipRecipe = useCallback(
    (
      recipe: Partial<LikedRecipe> & { id: string; title: string },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      // Don't change localStorage - just record the skip for learning
      recordInteraction(recipe, "skipped", additionalData);
    },
    [recordInteraction],
  );

  // PHASE 5: New interaction recording functions for expanded signals
  const viewRecipe = useCallback(
    (
      recipe: Partial<LikedRecipe> & { id: string; title: string },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      recordInteraction(recipe, "viewed", additionalData);
    },
    [recordInteraction],
  );

  const markRecipeCooked = useCallback(
    (
      recipe: Partial<LikedRecipe> & { id: string; title: string },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      recordInteraction(recipe, "cooked", additionalData);
    },
    [recordInteraction],
  );

  const shareRecipe = useCallback(
    (
      recipe: Partial<LikedRecipe> & { id: string; title: string },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      recordInteraction(recipe, "shared", additionalData);
    },
    [recordInteraction],
  );

  const markRecipeMaybe = useCallback(
    (
      recipe: Partial<LikedRecipe> & { id: string; title: string },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      recordInteraction(recipe, "maybe", additionalData);
    },
    [recordInteraction],
  );

  const toggleLikeRecipe = useCallback(
    async (
      recipe: Partial<LikedRecipe> & {
        id: string;
        title: string;
        imageUrl: string;
      },
      additionalData?: {
        prepTime?: number;
        calories?: number;
        tags?: string[];
        ingredients?: string[];
      },
    ) => {
      if (likedRecipeIds.includes(recipe.id)) {
        await unlikeRecipe(recipe.id, recipe, additionalData);
      } else {
        await likeRecipe(recipe, additionalData);
      }
    },
    [likedRecipeIds, likeRecipe, unlikeRecipe],
  );

  const isLiked = useCallback(
    (recipeId: string) => {
      return likedRecipeIds.includes(recipeId);
    },
    [likedRecipeIds],
  );

  const clearAllLikedRecipes = useCallback(async () => {
    try {
      // Clear database favorites (would need a new server action for batch delete)
      console.warn("clearAllLikedRecipes: Batch delete not implemented yet");
      // For now, clear localStorage migration data
      localStorage.removeItem(LIKED_RECIPES_KEY);
    } catch (error) {
      console.warn("Failed to clear liked recipes:", error);
    }
  }, []);

  return {
    likedRecipes,
    likedRecipeIds,
    likeRecipe,
    unlikeRecipe,
    skipRecipe, // Function to record skips without affecting database
    toggleLikeRecipe,
    isLiked,
    clearAllLikedRecipes,
    totalLiked: likedRecipes.length,
    isLoading, // Loading state from database query
    // PHASE 5: Expanded interaction tracking
    viewRecipe, // Record when user views a recipe detail
    markRecipeCooked, // Record when user completes cooking a recipe
    shareRecipe, // Record when user shares a recipe
    markRecipeMaybe, // Record when user marks recipe as "maybe later"
  };
}
